import axios from 'axios';
import LocalStorageServices from './helpers/LocalStorageServices';
import { SERVER_URL } from './serverUrl';

const api = axios.create({
  baseURL: SERVER_URL,
  timeout: 60 * 1000, // 1 minute
});

api.interceptors.request.use(
  (params) => {
    const token = LocalStorageServices.getToken();
    if (token) {
      params.headers.Authorization = `Bearer ${token}`;
    }
    return params;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (error?.response?.status === 401) {
      LocalStorageServices.removeToken();
    }
    return Promise.reject(error);
  },
);

class Api {
  static url = SERVER_URL;

  /* Account start */

  static login(data) {
    return api.post('/api/admins/login', data);
  }

  static getProfile(data) {
    return api.get('/api/admins/profile', data);
  }

  /* Account end */

  /* Organizations start */

  static getOrganizations(params, config = {}) {
    return api.get('/api/organizations', { params, ...config });
  }

  static getOrganization({ id }) {
    return api.get(`/api/organizations/${id}?should_add_clicks=false`);
  }

  static blockOrganization(data) {
    return api.put('/api/admins/organizations/block', data);
  }

  static deleteOrganization(data) {
    return api.post('/api/admins/organizations', data);
  }

  static changeOrganizationOwner(data) {
    return api.put('/api/admins/organizations/change-owner', data);
  }

  static createOrganization(formData) {
    return api.post('/api/admins/organizations/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static editOrganization({ id, payload }) {
    return api.patch(`/api/admins/organizations/edit/${id}`, payload);
  }

  static addOrganizationPhotos({ id, formData }) {
    return api.post(`/api/admins/organizations/${id}/photos/add`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static removeOrganizationPhotos({ id, payload }) {
    return api.post(`/api/admins/organizations/${id}/photos/remove`, payload);
  }

  /* Organizations end */

  /* Events start */

  static getEvents(params, config = {}) {
    return api.get('/api/events', { params, ...config });
  }

  static getEvent({ id }) {
    return api.get(`/api/events/${id}?should_add_clicks=false`);
  }

  static blockEvent(data) {
    return api.put('/api/admins/events/block', data);
  }

  static deleteEvent({ id }) {
    return api.delete(`/api/admins/events/${id}`);
  }

  static createEvent(formData) {
    return api.post('/api/admins/events/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static editEvent({ id, payload }) {
    return api.patch(`/api/admins/events/edit/${id}`, payload);
  }

  static addEventPhotos({ id, formData }) {
    return api.post(`/api/admins/events/${id}/photos/add`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static removeEventPhotos({ id, payload }) {
    return api.post(`/api/admins/events/${id}/photos/remove`, payload);
  }

  /* Events end */

  /* Users start */
  static getUsers(data) {
    return api.get('/api/admins/users', {
      params: data,
    });
  }

  static blockUser(data) {
    return api.put('/api/admins/users/block', data);
  }

  static deleteUser({ id }) {
    return api.delete(`/api/admins/users/${id}`);
  }

  /* Users end */

  /* Admins start */

  static getAdmins(data) {
    return api.get('/api/root/admins', {
      params: data,
    });
  }

  static editAdmin({ id, data }) {
    return api.put(`/api/root/admins/${id}`, data);
  }

  static addAdmin(data) {
    return api.post('/api/root/admins', data);
  }

  static deleteAdmin(id) {
    return api.delete(`/api/root/admins/${id}`);
  }

  /* Admins end */

  /* Static data start */

  static getCountries() {
    return api.get('/api/static-data/countries?sort=creation');
  }

  static getLanguages() {
    return api.get('/api/static-data/languages');
  }

  static getCategories() {
    return api.get('/api/static-data/categories-all');
  }

  static getCities(params) {
    return api.get('/api/static-data/cities', { params });
  }

  static getInfoTable(params) {
    return api.get('/api/static-data/info-table', { params });
  }

  /* Static data end */

  /* Admins start */

  static addCountry(formData) {
    return api.post('/api/admins/countries', formData);
  }

  static editCountry({ id, formData }) {
    return api.put(`/api/admins/countries/${id}`, formData);
  }

  static deleteCountry(id) {
    return api.delete(`/api/admins/countries/${id}`);
  }

  static addCity(data) {
    return api.post('/api/admins/cities', data);
  }

  static editCities(data) {
    return api.put('/api/admins/cities', { ...data, lala: 1111 });
  }

  static deleteCity(id) {
    return api.delete(`/api/admins/cities/${id}`);
  }

  /* Admins end */

  /* Categories start */

  static getAdminCategories(params) {
    return api.get('/api/admins/categories', { params });
  }

  static createAdminCategory(body) {
    return api.post('/api/admins/categories', body);
  }

  static renameAdminCategory({ id, ...body }) {
    return api.put(`/api/admins/categories/${id}/name`, body);
  }

  static editSortAdminCategory(body) {
    return api.put('/api/admins/categories/sort', body);
  }

  static editIconAdminCategory({ id, formData, params }) {
    return api.put(`/api/admins/categories/${id}/icon`, formData, { params });
  }

  static deleteAdminCategory({ id }) {
    return api.delete(`/api/admins/categories/${id}`);
  }

  static getAdminSubCategories(params) {
    return api.get('/api/admins/sub-categories', { params });
  }

  static createAdminSubCategory(body) {
    return api.post('/api/admins/sub-categories', body);
  }

  static renameAdminSubCategory({ id, ...body }) {
    return api.put(`/api/admins/sub-categories/${id}/name`, body);
  }

  static editSortAdminSubCategory(body) {
    return api.put('/api/admins/sub-categories/sort', body);
  }

  static deleteAdminSubCategory({ id }) {
    return api.delete(`/api/admins/sub-categories/${id}`);
  }

  /* Categories end */

  /* Articles start */

  static getArticleBlocks(params) {
    return api.get('/api/admins/article-blocks', { params });
  }

  static createArticleBlock(body) {
    return api.post('/api/admins/article-blocks', body);
  }

  static renameArticleBlock({ id, ...body }) {
    return api.put(`/api/admins/article-blocks/${id}/name`, body);
  }

  static editSortArticleBlocks(body) {
    return api.put('/api/admins/article-blocks/sort', body);
  }

  static deleteArticleBlock({ id, ...params }) {
    return api.delete(`/api/admins/article-blocks/${id}`, { params });
  }

  static getArticles(params) {
    return api.get('/api/admins/articles', { params });
  }

  static createArticle({ formData, ...params }) {
    return api.post('/api/admins/articles', formData, { params });
  }

  static editArticle({ formData, id, ...params }) {
    return api.put(`/api/admins/articles/${id}`, formData, { params });
  }

  static deleteArticle({ id, ...params }) {
    return api.delete(`/api/admins/articles/${id}`, { params });
  }

  /* Articles end */

  /* Gallery start */

  static getGalleryPhotos(params) {
    return api.get('/api/admins/gallery', { params });
  }

  static addPhotosToGallery(formData) {
    return api.post('/api/admins/gallery', formData);
  }

  static deletePhotosToGallery({ id, params }) {
    return api.delete(`/api/admins/gallery/${id}`, { params });
  }

  /* Gallery end */

  /* TemplatePhotos start */

  static getTemplatePhotosPhotos(params) {
    return api.get('/api/admins/template-photos', { params });
  }

  static getSingleTemplatePhoto({ id }) {
    return api.get(`/api/admins/template-photos/${id}`);
  }

  static addPhotosToTemplatePhotos(formData) {
    return api.post('/api/admins/template-photos', formData);
  }

  static deletePhotosToTemplatePhotos({ id, params }) {
    return api.delete(`/api/admins/template-photos/${id}`, { params });
  }

  /* TemplatePhotos end */

  /* Infos start */

  static editInfoItem(data) {
    return api.put('/api/admins/infos', data);
  }

  /* Infos end */

  /* Complaints start */

  static getComplaints({ params }) {
    return api.get('/api/admins/complaints', { params });
  }

  static getComplaintsCount({ params }) {
    return api.get('/api/admins/complaints/count', { params });
  }

  static answerComplain(data) {
    return api.put('/api/admins/complaints', data);
  }

  /* Complaints end */

  /* Geocoding */
  static getDropdownAddresses({ params, signal }) {
    return api.get('/api/admins/geocoding/complete-address', { params, signal });
  }

  static getAddressCoordinates({ params }) {
    return api.get('/api/admins/geocoding/get-place-coordinates', { params });
  }

  static getPlaceNameWithCoordinates({ params }) {
    return api.get('/api/admins/geocoding/get-place-name-with-coordinates', { params });
  }
  /* Geocoding */
}

export default Api;
